import React, { useState } from "react";
import PropTypes from "prop-types";
import { sanitizeUrl } from "../utils/formatters";
import "./WidgetPreview.css";

const WidgetPreview = ({
  widget = {
    display_name: "Chat Assistant",
    welcome_message: "Hello! How can I help you today?",
    branding: {
      primary_color: "#007bff",
      position: "bottom-right",
      input_placeholder: "Type a message...",
      show_branding: true,
    },
  },
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: widget?.welcome_message || "Hello! How can I help you today?",
    },
  ]);
  const [inputValue, setInputValue] = useState("");

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const newMessages = [
      ...messages,
      { role: "user", content: inputValue },
      {
        role: "assistant",
        content: "This is a preview - messages are not actually sent.",
      },
    ];

    setMessages(newMessages);
    setInputValue("");
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Extract customization from widget
  const primaryColor = widget?.branding?.primary_color || "#007bff";
  const position = widget?.branding?.position || "bottom-right";
  const rawAvatarUrl = widget?.branding?.avatar_url || null;
  // Validate avatar URL to prevent javascript: or data: URIs
  const avatarUrl = rawAvatarUrl ? sanitizeUrl(rawAvatarUrl) : null;
  const displayName = widget?.display_name || "Chat Assistant";

  // Position classes
  const getPositionClasses = () => {
    switch (position) {
      case "bottom-left":
        return "widget-preview-bottom-left";
      case "bottom-right":
        return "widget-preview-bottom-right";
      case "top-left":
        return "widget-preview-top-left";
      case "top-right":
        return "widget-preview-top-right";
      default:
        return "widget-preview-bottom-right";
    }
  };

  return (
    <div className="widget-preview-container">
      <div className="preview-label">
        <span>Live Preview</span>
        <span className="preview-hint">
          Customizations are reflected in real-time
        </span>
      </div>

      <div className={`widget-preview-wrapper ${getPositionClasses()}`}>
        {/* Trigger Button */}
        {!isOpen && (
          <button
            className="widget-trigger-btn"
            onClick={handleToggle}
            style={{ backgroundColor: primaryColor }}
            aria-label="Open chat"
          >
            💬
          </button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <div className="widget-chat-window">
            {/* Header */}
            <div
              className="widget-header"
              style={{ backgroundColor: primaryColor }}
            >
              <div className="widget-header-content">
                {avatarUrl && (
                  <img
                    src={avatarUrl}
                    alt="Assistant avatar"
                    className="widget-avatar"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <span className="widget-title">{displayName}</span>
              </div>
              <div className="widget-header-actions">
                <button
                  className="widget-minimize-btn"
                  onClick={handleClose}
                  aria-label="Minimize chat"
                >
                  −
                </button>
                <button
                  className="widget-close-btn"
                  onClick={handleClose}
                  aria-label="Close chat"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="widget-messages">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`widget-message widget-message-${message.role}`}
                >
                  {message.role === "assistant" && avatarUrl && (
                    <img
                      src={avatarUrl}
                      alt="Assistant"
                      className="message-avatar"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <div className="widget-message-content">
                    {message.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <form className="widget-input-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                className="widget-input"
                placeholder={
                  widget?.branding?.input_placeholder || "Type a message..."
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button
                type="submit"
                className="widget-send-btn"
                style={{ color: primaryColor }}
                aria-label="Send message"
              >
                ➤
              </button>
            </form>

            {/* Branding Footer */}
            {widget?.branding?.show_branding !== false && (
              <div className="widget-branding">
                Powered by <strong>Kwibl</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

WidgetPreview.propTypes = {
  widget: PropTypes.shape({
    display_name: PropTypes.string,
    welcome_message: PropTypes.string,
    branding: PropTypes.shape({
      primary_color: PropTypes.string,
      avatar_url: PropTypes.string,
      position: PropTypes.oneOf([
        "bottom-right",
        "bottom-left",
        "top-right",
        "top-left",
      ]),
      input_placeholder: PropTypes.string,
      show_branding: PropTypes.bool,
    }),
  }),
};

export default WidgetPreview;
